import { useEffect } from "react";
import CountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import { useMatch, useNavigate } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useResetAtomicState from "@hooks/useResetAtomicState";
import useSessionStateSync from "@hooks/useSessionStateSync";

import { faCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm } from "@inertiajs/react";
import { useAtom } from "jotai";

import {
    Button,
    Flex,
    Image,
    Link,
    Text,
    useDisclosure
} from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { CheckoutStateInterface } from "@ts/interfaces/Misc/state/CheckoutStateInterface";

import logoSvg from "@assets/img/logo.svg";

import { appAtom } from "@atoms/appAtom.ts";
import { checkoutAtom } from "@atoms/checkoutAtom";

import { getTrackingId } from "@utils/utils";

import { LocLangSelect } from "@components/LocLangSelect";

const CheckoutNav = () => {
    const { parentUser } = useCheckoutPageProps();

    const navigate = useNavigate();

    const { i18n } = useTranslation();

    const {
        isOpen: isLLOpen,
        onOpen: onOpenLLSelect,
        onClose: onCloseLLSelect
    } = useDisclosure();

    const matchUserInfoPage = useMatch(CheckoutPageRoutesEnum.USER_INFO_PAGE);
    const matchPaymentSuccessPage = useMatch(
        CheckoutPageRoutesEnum.PAYMENT_SUCCESS_PAGE
    );
    const matchExistingUserPage = useMatch(
        CheckoutPageRoutesEnum.EXISTING_USER_PAGE
    );
    const [app, setApp] = useAtom(appAtom);
    const [checkout] = useAtom(checkoutAtom);

    const [, sessionState] = useSessionStateSync<CheckoutStateInterface>();

    const { resetCheckoutAtomicState } = useResetAtomicState();

    const { post } = useForm({});

    const isParentUserLoggedIn = !!parentUser;

    const onResetCheckoutFlow = () => {
        // If a parent user logged in, we need to log them out as well
        if (isParentUserLoggedIn) {
            resetCheckoutAtomicState();
            post(route("logout.checkout"), {
                replace: true
            });
        } else {
            navigate(CheckoutPageRoutesEnum.USER_INFO_PAGE);
            resetCheckoutAtomicState({
                forceResetCheckoutAtom: true
            });
        }
    };

    useEffect(() => {
        // Setting the default language selected from i18n
        setApp(prev => ({ ...prev, language: i18n.language }));
    }, [i18n, setApp]);

    return (
        <>
            <Flex
                borderBottom="1px"
                borderBottomColor="gray.300"
                px={10}
                py={2}
                bg="gray.50"
                justify="space-between"
                className={ClassesEnum.NavBar}
            >
                <Link
                    href={
                        isParentUserLoggedIn
                            ? route("parents.dashboard")
                            : route("external.ecom.home")
                    }
                    className={ClassesEnum.NavItem}
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.NavBar, ClassesEnum.NavItem],
                        isParentUserLoggedIn
                            ? route("parents.dashboard")
                            : route("external.ecom.home")
                    )}
                >
                    <Image
                        src={logoSvg}
                        alt="Euka Logo"
                        pointerEvents="none"
                        userSelect="none"
                    />
                </Link>
                <Flex gap={10}>
                    {(matchUserInfoPage || matchExistingUserPage) && (
                        <Flex
                            justify="center"
                            align="center"
                            gap={2}
                            cursor="pointer"
                            onClick={onOpenLLSelect}
                            className={ClassesEnum.NavItem}
                            data-tracking-id={getTrackingId(
                                [ClassesEnum.NavBar, ClassesEnum.NavItem],
                                "openLocaleSelectionModal"
                            )}
                        >
                            <CountryFlag
                                countryCode={app.location}
                                svg
                                style={{
                                    width: 20,
                                    height: "auto",
                                    marginTop: "1px"
                                }}
                            />
                            <Text style={{ textTransform: "uppercase" }}>
                                {app.currency}
                            </Text>
                            <Text>|</Text>
                            <Text style={{ textTransform: "uppercase" }}>
                                {app.language}
                            </Text>
                        </Flex>
                    )}
                    <Flex
                        color="gray.900"
                        justify="center"
                        align="center"
                        gap={3}
                    >
                        {matchUserInfoPage && (
                            <Link
                                href={route("external.ecom.home")}
                                _hover={{ textDecoration: "none" }}
                            >
                                <Flex
                                    justify="center"
                                    align="center"
                                    cursor="pointer"
                                    gap={2}
                                    className={ClassesEnum.NavItem}
                                    data-tracking-id={getTrackingId(
                                        [
                                            ClassesEnum.NavBar,
                                            ClassesEnum.NavItem
                                        ],
                                        "goBack"
                                    )}
                                >
                                    <FontAwesomeIcon
                                        icon={faCircleLeft}
                                        color="var(--euka-colors-gray-600)"
                                        className="light"
                                        fontSize={16}
                                    />
                                    <Text
                                        fontSize={14}
                                        fontWeight="bold"
                                        fontFamily="infini, sans-serif"
                                    >
                                        Go back
                                    </Text>
                                </Flex>
                            </Link>
                        )}
                        {!matchUserInfoPage &&
                            !matchPaymentSuccessPage &&
                            !matchExistingUserPage &&
                            !sessionState.checkout?.checkoutPurchasingAddon && (
                                <>
                                    <Text>Hi, {checkout.user.firstName}</Text>
                                    <Button
                                        variant="link"
                                        w={20}
                                        px={0}
                                        fontFamily="ballinger, sans-serif"
                                        onClick={onResetCheckoutFlow}
                                        className={ClassesEnum.NavItem}
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.NavBar,
                                                ClassesEnum.NavItem
                                            ],
                                            "resetCheckout"
                                        )}
                                    >
                                        Not you?
                                    </Button>
                                </>
                            )}
                    </Flex>
                </Flex>
            </Flex>
            <LocLangSelect isOpen={isLLOpen} onClose={onCloseLLSelect} />
        </>
    );
};

export default CheckoutNav;
